import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { overrideVote } from '../data/votingRepository';
import { useRaceVotes, useVotingMutation } from '../hooks/useVoting';
import type { ElectionState, StateRace } from '../types';
import { candidateName } from '../utils/rounds';

const BLANK = '__blank__';

interface OverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: ElectionState;
  race: StateRace;
}

/** ADMIN: change the vote of someone who already voted in this (open) round */
export function OverrideDialog({ open, onOpenChange, state, race }: OverrideDialogProps) {
  const { data: votes = [], isLoading } = useRaceVotes(open ? race.id : undefined);
  const [filter, setFilter] = useState('');
  const [voterId, setVoterId] = useState<string | null>(null);
  const [candidateId, setCandidateId] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setFilter('');
      setVoterId(null);
      setCandidateId('');
      setNote('');
    }
  }, [open]);

  const save = useVotingMutation(
    state.election.id,
    () => overrideVote(race.id, voterId as string, candidateId === BLANK ? null : candidateId, note.trim()),
    { success: 'Voto corregido', extraKeys: [['voting', 'race-votes', race.id]] },
  );

  const q = filter.trim().toLowerCase();
  const list = votes.filter((v) => !q || v.full_name.toLowerCase().includes(q) || (v.ci ?? '').toLowerCase().includes(q));
  const voter = votes.find((v) => v.voter_id === voterId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Corregir un voto</DialogTitle>
          <DialogDescription>
            Solo mientras la ronda está abierta y solo para quien ya votó. Queda el motivo; el voto anterior no se conserva.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!voter ? (
            <div className="space-y-2">
              <Input placeholder="Buscar entre quienes votaron (nombre o CI)…" value={filter} onChange={(e) => setFilter(e.target.value)} />
              <div className="max-h-60 overflow-auto rounded-md border">
                {isLoading ? (
                  <div className="flex justify-center p-3"><Loader2 className="size-4 animate-spin" /></div>
                ) : list.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">Nadie coincide.</p>
                ) : (
                  list.map((v) => (
                    <button key={v.voter_id} type="button" onClick={() => setVoterId(v.voter_id)} className="block w-full px-3 py-2 text-left text-sm hover:bg-muted">
                      <span className="font-medium">{v.full_name}</span>{' '}
                      <span className="text-muted-foreground">· CI {v.ci ?? '—'} · votó por {candidateName(state, v.candidate_id)}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between rounded-md border p-3 text-sm">
                <span>
                  <strong>{voter.full_name}</strong> votó por <strong>{candidateName(state, voter.candidate_id)}</strong>
                </span>
                <Button size="sm" variant="ghost" onClick={() => setVoterId(null)}>Cambiar</Button>
              </div>
              <div className="space-y-1.5">
                <Label>Nuevo voto</Label>
                <Select value={candidateId} onValueChange={setCandidateId}>
                  <SelectTrigger><SelectValue placeholder="Elige el candidato" /></SelectTrigger>
                  <SelectContent>
                    {(race.candidate_ids ?? []).map((id) => (
                      <SelectItem key={id} value={id}>{candidateName(state, id)}</SelectItem>
                    ))}
                    {race.allow_blank && <SelectItem value={BLANK}>Voto en blanco</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ov-note">Motivo (obligatorio)</Label>
                <Textarea id="ov-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            disabled={!voter || !candidateId || note.trim().length === 0 || save.isPending}
            onClick={() => save.mutate(undefined, { onSuccess: () => onOpenChange(false) })}
          >
            {save.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            Guardar corrección
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
